import {
    AllUploadTypes,
    FileToUpload,
    LinkToUpload,
    MediaToUpload,
    MixedMedia,
    UploadType,
    UploadedMedia
} from '../types/Upload';
import { isImage, isLink } from './file';
import { trimURL } from './link';
import { getNonIntersection } from './array';

export type SortedMedia = {
    mediaToUpload: FileToUpload[];
    mediaToUpdate: UploadedMedia<UploadType.Image | UploadType.Document>[];
    mediaToDelete: UploadedMedia<UploadType.Image | UploadType.Document>[];
};

export const isFileToUpload = (upload: MixedMedia): upload is FileToUpload =>
    !isUploadedMedia(upload) && (upload.data instanceof File || upload.data instanceof Blob);

export const isUploadedImage = (
    upload: UploadedMedia<UploadType>
): upload is UploadedMedia<UploadType.Image> => upload.type === UploadType.Image;

export const isUploadedWeblink = (
    upload: UploadedMedia<UploadType>
): upload is UploadedMedia<UploadType.Weblink> => upload.type === UploadType.Weblink;

const isUploadedDocument = (
    upload: UploadedMedia<UploadType>
): upload is UploadedMedia<UploadType.Document> => upload.type === UploadType.Document;

const isUploadedImageOrDocument = (
    file: UploadedMedia<UploadType>
): file is UploadedMedia<UploadType.Document | UploadType.Image> =>
    isUploadedImage(file) || isUploadedDocument(file);

const filterOnlyMediaToUpload = (mixedMedia: MixedMedia[]) =>
    mixedMedia.filter((u) => !isUploadedMedia(u)) as MediaToUpload[];

export const filterOnlyUploadedMedia = (mixedMedia: MixedMedia[]) =>
    mixedMedia.filter(isUploadedMedia) as UploadedMedia<AllUploadTypes>[];

export const isUploadedMedia = (media: MixedMedia): media is UploadedMedia<AllUploadTypes> =>
    'uniqueMediaId' in media;

export const isLinkToUpload = (media: MixedMedia): media is LinkToUpload =>
    'data' in media && isLink(media.data);

export const mixedMediaIsLink = (
    media: MixedMedia
): media is UploadedMedia<UploadType.Weblink> | LinkToUpload =>
    isUploadedMedia(media) ? media.type === UploadType.Weblink : isLink(media.data);

const mapUploadToType = (data: string | Blob | File): UploadType => {
    if (isLink(data)) return UploadType.Weblink;
    if (isImage(data)) return UploadType.Image;
    else return UploadType.Document;
};

export const mapDraftMediaToUploaded = (file: MediaToUpload): UploadedMedia<AllUploadTypes> => {
    const { id, data, description, source } = file;
    const type = mapUploadToType(data);

    return {
        id,
        documentId: id.toString(),
        // We create a unique media id by combining the media type with it's id
        uniqueMediaId: `${type}_${id}`,
        type,
        name: isLink(data) ? data : (file as FileToUpload).name,
        url: isLink(data) ? data : URL.createObjectURL(data),
        description,
        source
    };
};

export const getMixedMediaFileSize = (media: MixedMedia) => {
    if (isUploadedMedia(media)) return media.size ?? 0;
    else if (isFileToUpload(media)) return media.data.size;
    else return 0;
};

export const getUploadName = (url: string, type: UploadType.Document | UploadType.Weblink) => {
    if (type === UploadType.Weblink) return trimURL(url);
    const fragments = url.split('/');

    return fragments[fragments.length - 1];
};

export const sortMediaUpdates = (
    oldUploads: UploadedMedia<UploadType>[],
    media: MixedMedia[]
): {
    mediaToUpload: FileToUpload[];
    mediaToUpdate: UploadedMedia<UploadType.Image | UploadType.Document>[];
    mediaToDelete: UploadedMedia<UploadType.Image | UploadType.Document>[];
} => {
    const mediaToUpload = filterOnlyMediaToUpload(media).filter(isFileToUpload);

    const mediaToDelete = filterOnlyUploadedMedia(getNonIntersection(oldUploads, media)).filter(
        isUploadedImageOrDocument
    );
    const filesInStrapi = filterOnlyUploadedMedia(media);

    // Find files that equal in id but changed in details to update them in STRAPI
    const mediaToUpdate = filesInStrapi
        .filter(
            (u) =>
                !mixedMediaIsLink(u) &&
                !!oldUploads.find((oldFile) => u.id === oldFile.id && u !== oldFile)
        )
        .filter(isUploadedImageOrDocument);

    return {
        mediaToUpload,
        mediaToDelete,
        mediaToUpdate
    };
};
